/**
 * DealDocumentChecklist — Document checklist for a deal
 *
 * Shows 4 document slots: OTP, ID Document, Bank Statement, Proof of Residence.
 * OTP slot opens a multi-step modal (Sellers → Property → Price & Terms → Channel).
 * Other slots open a simple channel-selection modal.
 * F-132 + F-133
 */

import { useState, useEffect, useCallback } from 'react';
import { getAPIBase, getAuthHeaders } from '../lib/api-fetch';
import { Button } from './ui/button';
import {
  FileText, Send, RefreshCw, Eye, CheckCircle2, Clock,
  AlertCircle, Circle, Loader2, ChevronRight, ChevronLeft, Plus, Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';

// ─── Types ────────────────────────────────────────────────────────────────────

type DocumentStatus = 'not_requested' | 'link_sent' | 'uploaded' | 'expired';

interface DocumentSlot {
  status: DocumentStatus;
  s3Key: string | null;
  sentAt: string | null;
  submittedAt: string | null;
  token: string | null;
  tokenExpiresAt: string | null;
}

interface DealDocuments {
  otp: DocumentSlot;
  idDocument: DocumentSlot;
  bankStatement: DocumentSlot;
  proofOfResidence: DocumentSlot;
}

export interface OtpData {
  seller1FullName: string;
  seller1IdNumber: string;
  seller1Capacity: string;
  seller1Address: string;
  hasSeller2: boolean;
  seller2FullName: string;
  seller2IdNumber: string;
  seller2Capacity: string;
  seller2Address: string;
  propertyType: 'conventional' | 'farm' | 'sectional';
  propertyAddress: string;
  propertySuburb: string;
  propertyCity: string;
  propertyPortion: string;
  propertyErf: string;
  propertyTownship: string;
  propertyFarmName: string;
  sectionUnitNumber: string;
  sectionDoorNumber: string;
  schemeName: string;
  ssNumber: string;
  purchasePrice: string;
  depositAmount: string;
  depositDate: string;
  bondRequired: boolean;
  bondAmount: string;
  bondDate: string;
  occupationDate: string;
  occupationalRental: string;
  agentCommissionPercent: string;
  agentName: string;
  agencyName: string;
  specialConditions: string;
  irrevocabilityDate: string;
  irrevocabilityTime: string;
}

const defaultOtpData: OtpData = {
  seller1FullName: '', seller1IdNumber: '', seller1Capacity: 'In my personal capacity', seller1Address: '',
  hasSeller2: false,
  seller2FullName: '', seller2IdNumber: '', seller2Capacity: 'In my personal capacity', seller2Address: '',
  propertyType: 'conventional',
  propertyAddress: '', propertySuburb: '', propertyCity: '',
  propertyPortion: '', propertyErf: '', propertyTownship: '',
  propertyFarmName: '',
  sectionUnitNumber: '', sectionDoorNumber: '', schemeName: '', ssNumber: '',
  purchasePrice: '', depositAmount: '', depositDate: '',
  bondRequired: false, bondAmount: '', bondDate: '',
  occupationDate: '', occupationalRental: '',
  agentCommissionPercent: '5.5', agentName: '', agencyName: '',
  specialConditions: '', irrevocabilityDate: '', irrevocabilityTime: '17:00',
};

const DOC_SLOTS = [
  { key: 'otp' as const, label: 'OTP (Offer to Purchase)', isOtp: true },
  { key: 'idDocument' as const, label: 'ID Document', isOtp: false },
  { key: 'bankStatement' as const, label: 'Bank Statement', isOtp: false },
  { key: 'proofOfResidence' as const, label: 'Proof of Residence', isOtp: false },
];

const DEFAULT_SLOT: DocumentSlot = {
  status: 'not_requested', s3Key: null, sentAt: null,
  submittedAt: null, token: null, tokenExpiresAt: null,
};

const CAPACITY_OPTIONS = [
  'In my personal capacity',
  'As trustee of a trust',
  'As director of a company',
  'As member of a close corporation',
];

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status, expiresAt }: { status: DocumentStatus; expiresAt?: string | null }) {
  if (status === 'uploaded') {
    return (
      <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
        <CheckCircle2 className="w-3.5 h-3.5" /> Uploaded
      </span>
    );
  }
  if (status === 'link_sent') {
    const expires = expiresAt
      ? new Date(expiresAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
      : '';
    return (
      <span className="flex items-center gap-1 text-amber-600 text-xs font-medium">
        <Clock className="w-3.5 h-3.5" /> Link sent{expires ? ` · expires ${expires}` : ''}
      </span>
    );
  }
  if (status === 'expired') {
    return (
      <span className="flex items-center gap-1 text-red-500 text-xs font-medium">
        <AlertCircle className="w-3.5 h-3.5" /> Expired
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-gray-400 text-xs">
      <Circle className="w-3.5 h-3.5" /> Not requested
    </span>
  );
}

// ─── Simple send modal (non-OTP docs) ─────────────────────────────────────────

function SendModal({
  open, onClose, docLabel, onSend, sending,
}: {
  open: boolean; onClose: () => void; docLabel: string;
  onSend: (channels: string[]) => void; sending: boolean;
}) {
  const [channels, setChannels] = useState<string[]>(['email']);
  const toggle = (ch: string) =>
    setChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Send {docLabel} Request</DialogTitle>
          <DialogDescription>Choose how to send the secure link to the client.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            {[{ id: 'whatsapp', label: 'WhatsApp' }, { id: 'email', label: 'Email' }].map(({ id, label }) => (
              <label key={id} className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50">
                <input type="checkbox" checked={channels.includes(id)} onChange={() => toggle(id)} className="w-4 h-4" />
                <span className="text-sm font-medium">{label}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={sending}>Cancel</Button>
            <Button className="flex-1" disabled={channels.length === 0 || sending} onClick={() => onSend(channels)}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── OTP send modal (4-step) ───────────────────────────────────────────────────

const OTP_STEPS = ['Seller Details', 'Property', 'Price & Terms', 'Send'];

function FieldRow({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function OtpSendModal({
  open, onClose, onSend, sending,
}: {
  open: boolean; onClose: () => void;
  onSend: (channels: string[], otpData: OtpData) => void; sending: boolean;
}) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<OtpData>(defaultOtpData);
  const [channels, setChannels] = useState<string[]>(['email']);
  const [validationError, setValidationError] = useState('');

  const set = (field: keyof OtpData, value: any) =>
    setData(prev => ({ ...prev, [field]: value }));

  const validate = (): string => {
    if (step === 0) {
      if (!data.seller1FullName.trim()) return 'Seller 1 full name is required';
      if (!data.seller1IdNumber.trim()) return 'Seller 1 ID number is required';
      if (!data.seller1Address.trim()) return 'Seller 1 address is required';
      if (data.hasSeller2) {
        if (!data.seller2FullName.trim()) return 'Seller 2 full name is required';
        if (!data.seller2IdNumber.trim()) return 'Seller 2 ID number is required';
      }
    }
    if (step === 1) {
      if (!data.propertyAddress.trim()) return 'Property address is required';
      if (!data.propertySuburb.trim()) return 'Suburb is required';
      if (!data.propertyCity.trim()) return 'City is required';
    }
    if (step === 2) {
      if (!data.purchasePrice.trim()) return 'Purchase price is required';
      if (!data.depositAmount.trim()) return 'Deposit amount is required';
      if (!data.depositDate) return 'Deposit due date is required';
      if (!data.occupationDate) return 'Occupation date is required';
    }
    if (step === 3) {
      if (channels.length === 0) return 'Select at least one channel';
    }
    return '';
  };

  const next = () => {
    const err = validate();
    if (err) { setValidationError(err); return; }
    setValidationError('');
    if (step < OTP_STEPS.length - 1) setStep(s => s + 1);
  };

  const back = () => { setValidationError(''); setStep(s => s - 1); };

  const handleSend = () => {
    const err = validate();
    if (err) { setValidationError(err); return; }
    onSend(channels, data);
  };

  const handleClose = () => {
    setStep(0);
    setData(defaultOtpData);
    setChannels(['email']);
    setValidationError('');
    onClose();
  };

  const toggleChannel = (ch: string) =>
    setChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send OTP Link</DialogTitle>
          <DialogDescription>Fill in the deal details the purchaser will see and sign.</DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 pb-2">
          {OTP_STEPS.map((label, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                i < step ? 'bg-indigo-600 text-white' :
                i === step ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-400' :
                'bg-gray-100 text-gray-400'
              }`}>{i + 1}</div>
              <span className={`text-xs hidden sm:block ${i === step ? 'text-indigo-700 font-medium' : 'text-gray-400'}`}>{label}</span>
              {i < OTP_STEPS.length - 1 && <div className="w-4 h-px bg-gray-200 mx-1" />}
            </div>
          ))}
        </div>

        {/* Step 0: Seller Details */}
        {step === 0 && (
          <div className="space-y-4">
            <p className="text-sm font-semibold text-gray-700">Seller 1</p>
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Full Name" required>
                <Input id="otp-seller1-fullname" value={data.seller1FullName} onChange={e => set('seller1FullName', e.target.value)} placeholder="As per ID document" aria-label="Full Name" />
              </FieldRow>
              <FieldRow label="ID / Passport Number" required>
                <Input id="otp-seller1-idnumber" value={data.seller1IdNumber} onChange={e => set('seller1IdNumber', e.target.value)} aria-label="ID Number" />
              </FieldRow>
            </div>
            <FieldRow label="Capacity">
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={data.seller1Capacity}
                onChange={e => set('seller1Capacity', e.target.value)}
              >
                {CAPACITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Residential Address" required>
              <Input value={data.seller1Address} onChange={e => set('seller1Address', e.target.value)} placeholder="Street, Suburb, City" />
            </FieldRow>

            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <input type="checkbox" checked={data.hasSeller2} onChange={e => set('hasSeller2', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm text-gray-700">Add a second seller</span>
              {data.hasSeller2 ? <Minus className="w-3.5 h-3.5 text-gray-400" /> : <Plus className="w-3.5 h-3.5 text-gray-400" />}
            </label>

            {data.hasSeller2 && (
              <div className="space-y-3 pl-3 border-l-2 border-indigo-100">
                <p className="text-sm font-semibold text-gray-700">Seller 2</p>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Full Name" required>
                    <Input value={data.seller2FullName} onChange={e => set('seller2FullName', e.target.value)} />
                  </FieldRow>
                  <FieldRow label="ID / Passport Number" required>
                    <Input value={data.seller2IdNumber} onChange={e => set('seller2IdNumber', e.target.value)} />
                  </FieldRow>
                </div>
                <FieldRow label="Capacity">
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={data.seller2Capacity}
                    onChange={e => set('seller2Capacity', e.target.value)}
                  >
                    {CAPACITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </FieldRow>
                <FieldRow label="Residential Address">
                  <Input value={data.seller2Address} onChange={e => set('seller2Address', e.target.value)} />
                </FieldRow>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Property Details */}
        {step === 1 && (
          <div className="space-y-4">
            <FieldRow label="Property Type">
              <div className="flex gap-2">
                {(['conventional', 'farm', 'sectional'] as const).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set('propertyType', t)}
                    className={`flex-1 py-1.5 rounded-md border text-sm capitalize ${
                      data.propertyType === t ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t === 'sectional' ? 'Sectional Title' : t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </FieldRow>

            <FieldRow label="Street Address" required>
              <Input value={data.propertyAddress} onChange={e => set('propertyAddress', e.target.value)} placeholder="e.g. 12 Oak Avenue" />
            </FieldRow>
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Suburb" required>
                <Input value={data.propertySuburb} onChange={e => set('propertySuburb', e.target.value)} />
              </FieldRow>
              <FieldRow label="City" required>
                <Input value={data.propertyCity} onChange={e => set('propertyCity', e.target.value)} />
              </FieldRow>
            </div>

            {data.propertyType === 'conventional' && (
              <div className="grid grid-cols-3 gap-3">
                <FieldRow label="Erf Number">
                  <Input value={data.propertyErf} onChange={e => set('propertyErf', e.target.value)} />
                </FieldRow>
                <FieldRow label="Portion">
                  <Input value={data.propertyPortion} onChange={e => set('propertyPortion', e.target.value)} />
                </FieldRow>
                <FieldRow label="Township">
                  <Input value={data.propertyTownship} onChange={e => set('propertyTownship', e.target.value)} />
                </FieldRow>
              </div>
            )}

            {data.propertyType === 'farm' && (
              <FieldRow label="Farm Name">
                <Input value={data.propertyFarmName} onChange={e => set('propertyFarmName', e.target.value)} />
              </FieldRow>
            )}

            {data.propertyType === 'sectional' && (
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="Unit Number">
                  <Input value={data.sectionUnitNumber} onChange={e => set('sectionUnitNumber', e.target.value)} />
                </FieldRow>
                <FieldRow label="Door Number">
                  <Input value={data.sectionDoorNumber} onChange={e => set('sectionDoorNumber', e.target.value)} />
                </FieldRow>
                <FieldRow label="Scheme Name">
                  <Input value={data.schemeName} onChange={e => set('schemeName', e.target.value)} />
                </FieldRow>
                <FieldRow label="SS Number">
                  <Input value={data.ssNumber} onChange={e => set('ssNumber', e.target.value)} />
                </FieldRow>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Price & Terms */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Purchase Price (R)" required>
                <Input
                  value={data.purchasePrice}
                  onChange={e => set('purchasePrice', e.target.value)}
                  placeholder="e.g. 1500000"
                  type="number"
                />
              </FieldRow>
              <FieldRow label="Deposit Amount (R)" required>
                <Input
                  value={data.depositAmount}
                  onChange={e => set('depositAmount', e.target.value)}
                  type="number"
                />
              </FieldRow>
              <FieldRow label="Deposit Due Date" required>
                <Input value={data.depositDate} onChange={e => set('depositDate', e.target.value)} type="date" />
              </FieldRow>
              <FieldRow label="Occupation Date" required>
                <Input value={data.occupationDate} onChange={e => set('occupationDate', e.target.value)} type="date" />
              </FieldRow>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={data.bondRequired} onChange={e => set('bondRequired', e.target.checked)} className="w-4 h-4" />
              <span className="text-sm text-gray-700">Bond required?</span>
            </label>

            {data.bondRequired && (
              <div className="grid grid-cols-2 gap-3 pl-3 border-l-2 border-indigo-100">
                <FieldRow label="Bond Amount (R)">
                  <Input value={data.bondAmount} onChange={e => set('bondAmount', e.target.value)} type="number" />
                </FieldRow>
                <FieldRow label="Bond Approval Deadline">
                  <Input value={data.bondDate} onChange={e => set('bondDate', e.target.value)} type="date" />
                </FieldRow>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Occupational Rental (R/month)">
                <Input value={data.occupationalRental} onChange={e => set('occupationalRental', e.target.value)} type="number" placeholder="0" />
              </FieldRow>
              <FieldRow label="Commission %">
                <Input value={data.agentCommissionPercent} onChange={e => set('agentCommissionPercent', e.target.value)} type="number" step="0.5" />
              </FieldRow>
              <FieldRow label="Irrevocability Date">
                <Input value={data.irrevocabilityDate} onChange={e => set('irrevocabilityDate', e.target.value)} type="date" />
              </FieldRow>
              <FieldRow label="Irrevocability Time">
                <Input value={data.irrevocabilityTime} onChange={e => set('irrevocabilityTime', e.target.value)} type="time" />
              </FieldRow>
            </div>

            <FieldRow label="Agent Name">
              <Input value={data.agentName} onChange={e => set('agentName', e.target.value)} />
            </FieldRow>
            <FieldRow label="Agency Name">
              <Input value={data.agencyName} onChange={e => set('agencyName', e.target.value)} />
            </FieldRow>
            <FieldRow label="Special Conditions">
              <textarea
                className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px] resize-none"
                value={data.specialConditions}
                onChange={e => set('specialConditions', e.target.value)}
                placeholder="Any special conditions (leave blank if none)"
              />
            </FieldRow>
          </div>
        )}

        {/* Step 3: Channel & Send */}
        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The purchaser will receive a secure link to review and sign the OTP agreement.
            </p>
            <div className="space-y-2">
              {[{ id: 'whatsapp', label: 'WhatsApp' }, { id: 'email', label: 'Email' }].map(({ id, label }) => (
                <label key={id} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border hover:bg-gray-50">
                  <input type="checkbox" checked={channels.includes(id)} onChange={() => toggleChannel(id)} className="w-4 h-4" />
                  <span className="text-sm font-medium">{label}</span>
                </label>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              The link will expire in 48 hours. The purchaser can only submit it once.
            </div>
          </div>
        )}

        {/* Validation error */}
        {validationError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {validationError}
          </p>
        )}

        {/* Navigation */}
        <div className="flex gap-2 pt-2">
          {step > 0 && (
            <Button variant="outline" onClick={back} disabled={sending}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          <Button variant="outline" className="ml-auto" onClick={handleClose} disabled={sending}>Cancel</Button>
          {step < OTP_STEPS.length - 1 ? (
            <Button onClick={next}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button disabled={channels.length === 0 || sending} onClick={handleSend}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send OTP Link
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function DealDocumentChecklist({ dealId }: { dealId: string }) {
  const [documents, setDocuments] = useState<DealDocuments | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [sendModalKey, setSendModalKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${getAPIBase()}/deals/${dealId}/documents`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setDocuments(json.data.documents);
    } catch {
      // fail silently — show empty state
    } finally {
      setIsLoading(false);
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  const sendLink = async (documentType: string, channels: string[], otpData?: OtpData) => {
    setSendingKey(documentType);
    try {
      const res = await fetch(`${getAPIBase()}/deals/${dealId}/documents/send-link`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentType, channels, ...(otpData ? { otpData } : {}) }),
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Link sent successfully');
        await load();
      } else {
        toast.error(json.message || 'Failed to send link');
      }
    } catch {
      toast.error('Failed to send link');
    } finally {
      setSendingKey(null);
      setOtpModalOpen(false);
      setSendModalKey(null);
    }
  };

  const viewDocument = (slot: DocumentSlot) => {
    if (slot.s3Key) {
      toast.info('Document viewer coming soon');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse">
        {DOC_SLOTS.map(s => (
          <div key={s.key} className="h-12 bg-gray-100 rounded-lg" />
        ))}
      </div>
    );
  }

  const activeSlot = sendModalKey ? DOC_SLOTS.find(s => s.key === sendModalKey) : null;

  return (
    <div className="space-y-2" data-testid="deal-document-checklist">
      {DOC_SLOTS.map(({ key, label, isOtp }) => {
        const slot: DocumentSlot = documents?.[key] ?? DEFAULT_SLOT;
        const { status } = slot;
        const isSending = sendingKey === key;

        return (
          <div
            key={key}
            className={`flex items-center justify-between p-3 rounded-lg border ${
              status === 'uploaded' ? 'border-emerald-200 bg-emerald-50/50' :
              status === 'link_sent' ? 'border-amber-200 bg-amber-50/50' :
              status === 'expired' ? 'border-red-200 bg-red-50/50' :
              'border-gray-200 bg-white'
            }`}
            data-testid={`deal-doc-${key}`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <FileText className={`w-4 h-4 flex-shrink-0 ${
                status === 'uploaded' ? 'text-emerald-500' :
                status === 'expired' ? 'text-red-400' : 'text-gray-400'
              }`} />
              <div>
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <StatusBadge status={status} expiresAt={slot.tokenExpiresAt} />
              </div>
            </div>

            <div className="flex-shrink-0">
              {status === 'uploaded' ? (
                <Button
                  variant="outline" size="sm"
                  onClick={() => viewDocument(slot)}
                  data-testid={`deal-doc-${key}-view`}
                  className="text-xs"
                >
                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                </Button>
              ) : status === 'link_sent' || status === 'expired' ? (
                <Button
                  variant="outline" size="sm" disabled={isSending}
                  onClick={() => isOtp ? setOtpModalOpen(true) : setSendModalKey(key)}
                  data-testid={`deal-doc-${key}-resend`}
                  className="text-xs"
                >
                  {isSending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                  Resend
                </Button>
              ) : (
                <Button
                  size="sm" disabled={isSending}
                  onClick={() => isOtp ? setOtpModalOpen(true) : setSendModalKey(key)}
                  data-testid={isOtp ? 'send-otp-link-btn' : `deal-doc-${key}-send`}
                  className="text-xs"
                >
                  {isSending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                  {isOtp ? 'Send OTP Link' : 'Send Request Link'}
                </Button>
              )}
            </div>
          </div>
        );
      })}

      {/* OTP send modal (multi-step) */}
      <OtpSendModal
        open={otpModalOpen}
        onClose={() => setOtpModalOpen(false)}
        onSend={(channels, otpData) => sendLink('otp', channels, otpData)}
        sending={sendingKey === 'otp'}
      />

      {/* Simple send modal (non-OTP docs) */}
      <SendModal
        open={!!sendModalKey}
        onClose={() => setSendModalKey(null)}
        docLabel={activeSlot?.label ?? ''}
        onSend={(channels) => sendLink(sendModalKey!, channels)}
        sending={!!sendingKey}
      />
    </div>
  );
}
